<script setup lang="ts">
import type { PastGradeListDetail } from '#shared/interfaces/gradeList'
import type {
  SendBrandDetail,
  SendGradeDetail,
  SendModelGradesDetail,
  SendCondition,
  SendCar,
  SendNewcarSearch,
} from '@/interfaces/searchUsedCar'
import { TRANSITION_SOURCE } from '#shared/constant/constant'
import { postWindowBridge } from '@/utils/windowBridge'
const {
  specificationList = undefined,
  currentModelFlg = undefined,
  isUsedCars,
} = defineProps<{
  specificationList?: PastGradeListDetail[]
  currentModelFlg?: boolean
  isUsedCars: boolean
}>()
const nuxtApp = useNuxtApp()
const clickTag = ref(false)
const query = useRoute().query

const missionCd = ref('')
const missionDetailCd = ref('')
const changeMisiionCd = (searchMissionCd: number) => {
  const resultNewMissionCd = nuxtApp.$changeMissionCd(searchMissionCd)
  // 戻り値は [missionCd] または [missionCd, missionDetailCd] のため、存在する要素だけ反映する
  const [missionCdValue, missionDetailCdValue] = resultNewMissionCd ?? []
  if (missionCdValue !== undefined) {
    missionCd.value = missionCdValue
  }
  if (missionDetailCdValue !== undefined) {
    missionDetailCd.value = missionDetailCdValue
  }
}
const tableWidthPx = useState('table_width_px')

const searchUsedCars = (grade: PastGradeListDetail) => {
  const eventName = currentModelFlg
    ? 'CatalogCurrentMdlTopCarSearchTap'
    : 'CatalogPastMdlTopCarSearchTap'
  nuxtApp.$sendEventGaTag(eventName)

  changeMisiionCd(grade.search_mission_cd)
  const sendBrandDetail: SendBrandDetail = {
    name: grade.brand_name,
    brandCd: String(grade.brand_cd),
  }

  const sendGradeDetail: SendGradeDetail = {
    name: grade.grade_name,
    gradeCd: String(grade.grade_cd),
  }

  const sendModelGradesDetail: SendModelGradesDetail = {
    carCd: String(grade.car_cd),
    modelCd: String(grade.model_cd),
    grades: [sendGradeDetail],
  }

  const sendCars: SendCar = {
    name: grade.car_name ?? '',
    integrationCarCd: grade.car_cd
      ? nuxtApp.$convertCarCode(grade.car_cd.toString()) + '|'
      : '',
  }

  const sendCondition: SendCondition = {
    brand: [sendBrandDetail],
    car: [sendCars],
    modelGrades: [sendModelGradesDetail],
    mission: missionCd.value,
  }
  if (missionDetailCd.value) {
    sendCondition.missionDetail = missionDetailCd.value
  }
  if (grade.riding_capacity) {
    sendCondition.minRidingCapacity = grade.riding_capacity
    sendCondition.maxRidingCapacity = grade.riding_capacity
  }
  let sendDrive: string = ''
  if (
    grade.drive === 'FF' ||
    grade.drive === 'FR' ||
    grade.drive === 'RR' ||
    grade.drive === 'ミッドシップ'
  ) {
    sendDrive = '1'
  } else if (
    grade.drive == 'フルタイム４WD' ||
    grade.drive == 'パートタイム４WD'
  ) {
    sendDrive = '2'
  }
  if (sendDrive && sendDrive !== '') {
    sendCondition.driveSystem = sendDrive
  }
  postWindowBridge('transitionCarList', JSON.stringify(sendCondition))
}

let displayCarPrice: (string | number)[] = []
const createPrice = (
  underPrice: string | undefined,
  upperPrice: string | undefined
) => {
  displayCarPrice = nuxtApp.$createDisplayCarPrice(
    Number(underPrice),
    Number(upperPrice)
  )
  return displayCarPrice.length === 2
}

const createNewCarPrice = (price: string) => {
  if (!price) {
    return 'ーー'
  }
  return Number(price).toLocaleString()
}

const sendEventGaTag = (catalogId: number) => {
  if (!clickTag.value) {
    clickTag.value = true
    const eventName = currentModelFlg
      ? 'CatalogCurrentMdlTopGradeNameTap'
      : 'CatalogPastMdlTopGradeNameTap'
    nuxtApp.$sendEventGaTag(eventName)
    if (query.transition_source === TRANSITION_SOURCE.newcar_search) {
      const sendNewcarSearch: SendNewcarSearch = {
        url: `${useRuntimeConfig().public.BASE_URL}/model-grade/${catalogId}`,
      }
      if (
        postWindowBridge(
          'transitionCatalogApp',
          JSON.stringify(sendNewcarSearch)
        )
      ) {
        clickTag.value = false
        return
      }
    }
    navigateTo({
      path: '/model-grade/' + catalogId,
    })
  }
}

const checkDebutDate = (debutYear: number, debutMonth: number) => {
  return debutYear && debutMonth ? debutYear + '年' + debutMonth + '月' : '-'
}

onMounted(() => {
  clickTag.value = false
})
</script>
<template>
  <div v-if="specificationList" class="contents">
    <div class="grade-index">
      <ClientOnly>
        <table class="main-table">
          <tr class="wrapper">
            <td class="wrapper">
              <table class="table title-table">
                <tr class="row-title">
                  <td class="grade-title" colspan="2">グレード名</td>
                </tr>
                <tr class="left-row">
                  <td class="column-title" rowspan="12">
                    <span>基本情報</span>
                  </td>
                  <td class="sub-title">新車価格</td>
                </tr>
                <tr>
                  <td class="sub-title">中古車価格</td>
                </tr>
                <tr>
                  <td class="sub-title">発売日</td>
                </tr>
                <tr>
                  <td class="sub-title">新車販売状況</td>
                </tr>
                <tr>
                  <td class="sub-title">駆動方式</td>
                </tr>
                <tr>
                  <td class="sub-title">燃料</td>
                </tr>
                <tr>
                  <td class="sub-title">排気量</td>
                </tr>
                <tr>
                  <td class="sub-title row-2">トランスミッション</td>
                </tr>
                <tr>
                  <td class="sub-title">乗車定員</td>
                </tr>
                <tr>
                  <td class="sub-title">燃費(WLTC)</td>
                </tr>
                <tr>
                  <td class="sub-title">ハンドル位置</td>
                </tr>
                <tr>
                  <td class="sub-title">最小回転半径</td>
                </tr>
                <tr>
                  <td class="column-title" rowspan="3">
                    <span>エンジン</span>
                  </td>
                  <td class="sub-title row-2 top-border">
                    最高出力 (kW[PS]/rpm)
                  </td>
                </tr>
                <tr>
                  <td class="sub-title row-3">
                    最大トルク (N•m[kgf•m]/<br />rpm)
                  </td>
                </tr>
                <tr>
                  <td class="sub-title">過給機</td>
                </tr>
                <tr>
                  <td class="column-title" rowspan="4">
                    <span>寸法・重量</span>
                  </td>
                  <td class="sub-title top-border">全長</td>
                </tr>
                <tr>
                  <td class="sub-title">全幅</td>
                </tr>
                <tr>
                  <td class="sub-title">全高</td>
                </tr>
                <tr>
                  <td class="sub-title">車両重量</td>
                </tr>
                <tr>
                  <td class="column-title" rowspan="2">
                    <span>タイヤ</span>
                  </td>
                  <td class="sub-title top-border">前輪サイズ</td>
                </tr>
                <tr>
                  <td class="sub-title">後輪サイズ</td>
                </tr>
                <tr v-if="isUsedCars">
                  <td class="column-title" />
                  <td class="grade-info empty-cell top-border" />
                </tr>
                <tr />
              </table>
            </td>
          </tr>
          <tr>
            <td
              v-for="(grade, index) in specificationList"
              :key="grade.catalog_id"
              class="grade-row"
            >
              <tbody>
                <table
                  class="row detail-table"
                  :class="
                    specificationList.length === index + 1 ? 'last-table' : ''
                  "
                >
                  <tr class="row-title">
                    <td class="grade">
                      <div
                        class="underscore-link"
                        @click="sendEventGaTag(grade.catalog_id)"
                      >
                        {{ grade.grade_name }}
                      </div>
                    </td>
                  </tr>
                  <td class="grade-info new-car-price">
                    {{ createNewCarPrice(grade.price) }}円
                  </td>
                  <td
                    v-if="
                      createPrice(
                        grade.usedcar_total_price_lower_limit,
                        grade.usedcar_total_price_upper_limit
                      )
                    "
                    class="grade-info"
                  >
                    <div class="old-price-block">
                      <div class="old-price-lower">
                        {{
                          (Number(displayCarPrice[0]) * 10000).toLocaleString()
                        }}円〜
                      </div>
                      <div class="old-price-upper">
                        {{
                          (Number(displayCarPrice[1]) * 10000).toLocaleString()
                        }}円
                      </div>
                    </div>
                  </td>
                  <td v-else class="grade-info">
                    <span class="old-price-lower">{{
                      displayCarPrice[0] === 'ーー'
                        ? displayCarPrice[0]
                        : (Number(displayCarPrice[0]) * 10000).toLocaleString()
                    }}</span
                    ><span class="currency">円</span>
                  </td>
                  <td class="grade-info">
                    {{ checkDebutDate(grade.debut_year, grade.debut_month) }}
                  </td>
                  <td v-if="grade.current_model_flg" class="grade-info">
                    販売中
                  </td>
                  <td v-else class="grade-info">新車販売終了</td>
                  <td class="grade-info">
                    {{ $checkText(grade.drive) }}
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.fuel_name) }}
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.exhaust).toLocaleString() }}cc
                  </td>
                  <td class="grade-info row-2">
                    {{ $checkText(grade.mission_type) }}
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.riding_capacity) }}名
                  </td>
                  <td
                    v-if="grade.fuel_consumption_wltc_summary"
                    class="grade-info"
                  >
                    {{ $checkText(grade.fuel_consumption_wltc_summary) }}km/L
                  </td>
                  <td
                    v-else-if="grade.fuel_consumption_jc08"
                    class="grade-info"
                  >
                    {{ $checkText(grade.fuel_consumption_jc08) }}km/L
                  </td>
                  <td v-else-if="grade.fuel_consumption" class="grade-info">
                    {{ $checkText(grade.fuel_consumption) }}km/L
                  </td>
                  <td v-else class="grade-info">-km/L</td>
                  <td class="grade-info">
                    {{ $checkText(grade.handle_position) }}
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.min_turning_radius) }}m
                  </td>
                  <td class="grade-info row-2 top-border">
                    {{ $checkText(grade.max_power_kw) }}[{{
                      $checkText(grade.max_power_ps)
                    }}]/{{ $checkText(grade.max_power_rev) }}
                  </td>
                  <td class="grade-info row-3">
                    {{ $checkText(grade.max_torque_nm) }}[{{
                      $checkText(grade.max_torque)
                    }}]/{{ $checkText(grade.max_torque_rev) }}
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.supercharger) }}
                  </td>
                  <td class="grade-info top-border">
                    {{ $checkText(grade.length).toLocaleString() }}mm
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.width).toLocaleString() }}mm
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.height).toLocaleString() }}mm
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.weight).toLocaleString() }}kg
                  </td>
                  <td class="grade-info top-border">
                    {{ $checkText(grade.front_tire) }}
                  </td>
                  <td class="grade-info">
                    {{ $checkText(grade.rear_tire) }}
                  </td>
                  <td v-if="isUsedCars" class="btn-search-old-car top-border">
                    <button
                      class="btnred shadow-sm rounded"
                      @click="searchUsedCars(grade)"
                    >
                      <a>中古車を探す</a>
                    </button>
                  </td>
                </table>
              </tbody>
            </td>
          </tr>
        </table>
      </ClientOnly>
    </div>
  </div>
</template>
<style scoped>
.main-table {
  display: flex;
  width: v-bind(tableWidthPx);
}
.last-table {
  margin-right: 0px;
}
.wrapper {
  padding: 0px;
}
.title-table {
  border: 1px solid #f4f4f4;
  margin-top: 12px;
}
.detail-table {
  align-items: center;
  border-right: 1px solid #f4f4f4;
  border-bottom: 1px solid #f4f4f4;
  width: 133px;
}
.grade-row {
  padding: 12px;
}
.contents {
  font-size: 11px;
  padding: 16px 0px 16px 9px;
  margin-top: 16px;
  background-color: #ffffff;
}
.grade-index {
  overflow: auto;
}
.grade-index::-webkit-scrollbar {
  display: none;
}
.row-title {
  background-color: #f7f8fa;
  padding: 0;
}
.row-title .grade-title {
  width: 133px;
  padding-left: 7px;
  height: 103px;
}
.wrapper {
  position: sticky;
  left: 0;
}
.row-title .grade {
  width: 133px;
  height: 103px;
  color: #084ccee5;
  text-align: center;
}
.row-title .grade-detail {
  width: 133px;
  color: #084ccee5;
  padding-top: 26px;
  padding-bottom: 26px;
  padding-left: 7px;
  text-align: center;
}
.top-border {
  border-top: 1px solid #f4f4f4 !important;
}
.row-2 {
  height: 50px !important;
}
.row-3 {
  height: 66.5px !important;
}

.column-title {
  vertical-align: top;
  padding: 3px;
  width: 24px;
  border: 1px solid #f4f4f4 !important;
  background-color: #f7f8fa;
}
.column-title span {
  width: 16.5px;
  padding: 0;
  white-space: pre;
  -webkit-writing-mode: vertical-rl;
  -ms-writing-mode: tb-rl;
  writing-mode: vertical-rl;
}
.sub-title {
  max-width: 85px;
  height: 33.5px;
  padding-left: 7px;
  padding-right: 4px;
  vertical-align: middle;
  border: none;
  background-color: #ffffff;
}
.new-car-price {
  font-size: 11px;
  font-weight: bold;
  color: #df2020;
}
.currency {
  font-size: 11px;
  color: black;
}
.old-price-block {
  width: fit-content;
}
.old-price-lower {
  font-size: 11px;
  font-weight: bold;
}
.wave-line {
  font-size: 11px;
  font-weight: bold;
}
.old-price-upper {
  font-size: 11px;
  font-weight: bold;
}
.empty-cell {
  height: 53.5px !important;
  background-color: #ffffff;
}
.grade-info {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 33.5px;
  padding: 8px;
}
.grade-info.btn {
  height: 53.5px;
  background-color: #ffffff;
}
.btn-search-old-car {
  padding-top: 10px;
  padding-bottom: 10px;
  height: 53.5px;
}
.btnred {
  width: 111px;
  height: 30px;
  background: #df2020;
  font-weight: bold;
  border: none;
}
.btnred a {
  color: #ffffff !important;
}
</style>
